import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { ErrorBox, StatusChip } from '../components/Layout';
import { laoDateTime, money } from '../lib/money';
import { STATUS_LABEL, type Overview } from '../types';

/** ໂຫຼດໃໝ່ທຸກ 10 ວິນາທີ ເພື່ອໃຫ້ເຫັນເງິນເຂົ້າເກືອບທັນທີ */
const REFRESH_MS = 10_000;

export function OverviewPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = () =>
      api
        .overview()
        .then((value) => {
          if (!active) return;
          setData(value);
          setError(null);
        })
        .catch((err: unknown) => {
          if (active) setError(err instanceof ApiError ? err.message : 'ໂຫຼດຂໍ້ມູນບໍ່ໄດ້');
        });

    void load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (!data) {
    return (
      <>
        <ErrorBox error={error} />
        {!error && <p className="muted">ກຳລັງໂຫຼດ…</p>}
      </>
    );
  }

  const tiles: [string, { count: number; total: string }][] = [
    ['ມື້ນີ້', data.today],
    ['ອາທິດນີ້', data.week],
    ['ເດືອນນີ້', data.month],
    ['ລໍຖ້າຈ່າຍ', data.pending],
  ];

  return (
    <>
      <ErrorBox error={error} />

      {!data.merchantReady && (
        <p className="notice">
          ຍັງບໍ່ໄດ້ຕັ້ງຄ່າບັນຊີຮັບເງິນ — <Link to="/settings">ໄປຕັ້ງຄ່າ</Link>
        </p>
      )}

      <div className="stat-row four">
        {tiles.map(([label, value]) => (
          <div className="stat" key={label}>
            <span className="muted">{label}</span>
            <strong>{money(value.total)}</strong>
            <span className="muted">{value.count} ລາຍການ</span>
          </div>
        ))}
      </div>

      <section>
        <h2 className="section-title">ລາຍການທີ່ລໍຖ້າຈ່າຍ</h2>
        {data.pending.payments.length === 0 ? (
          <p className="muted">ບໍ່ມີລາຍການຄ້າງ</p>
        ) : (
          <ul className="admin-orders">
            {data.pending.payments.map((p) => (
              <li className="card admin-order" key={p.paymentId}>
                <Link to={`/payments/${p.paymentId}`} className="row-link">
                  <div className="admin-order-head">
                    <strong>{money(p.amount)}</strong>
                    <StatusChip status={p.status} label={STATUS_LABEL[p.status] ?? p.status} />
                  </div>
                  <p className="muted">
                    {p.description}
                    {p.orderNo && ` · ${p.orderNo}`} · ໝົດອາຍຸ {laoDateTime(p.expiresAt)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="section-title">ຮັບໄດ້ 30 ມື້ຫຼ້າສຸດ</h2>
        {data.daily.length === 0 ? (
          <p className="muted">ຍັງບໍ່ມີເງິນເຂົ້າ</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>ວັນທີ</th>
                <th className="num">ລາຍການ</th>
                <th className="num">ຍອດ</th>
              </tr>
            </thead>
            <tbody>
              {[...data.daily].reverse().map((d) => (
                <tr key={d.day}>
                  <td>{d.day}</td>
                  <td className="num">{d.count}</td>
                  <td className="num">{money(d.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
